/**
 * FIC subject autocomplete — debounced search hook (B-18).
 *
 * `GET /v1/trusts/{trustId}/subjects?q=` searches a trust's
 * beneficial-ownership + beneficiary registers so the MLRO can pick the
 * subject of a FIC report by name instead of pasting a raw record id (closes
 * F02-X-100 — the "user-12345…" placeholder). Backend handler:
 * `backend/src/handlers/trusts-subjects-search.ts`.
 *
 * Why a wrapper over the raw `useTrustSubjects` in `./trusts`:
 *   - the combobox feeds keystrokes straight in, so we debounce HERE (one
 *     place, unit-testable) rather than in the component — a request fires
 *     only after the user pauses;
 *   - a single-char query is too broad on a small register and just churns
 *     the network, so the fetch is gated behind a minimum query length
 *     (`minChars`, default 1 — an empty query still lists everything when the
 *     dropdown is open, but a 1-char prefix is allowed since registers are
 *     small and the backend caps results at 20).
 *
 * The debounce is internal (a piece of `useState` advanced by a `setTimeout`),
 * so callers pass the *raw* input text + the open flag and get back the
 * TanStack Query result plus the `debouncedQuery` actually in flight. The
 * underlying request, cache key, masking + result shape all live in
 * `./trusts` (`useTrustSubjects` / `TrustSubject`) and are reused verbatim.
 */

import { useEffect, useState } from 'react';
import { useTrustSubjects } from './trusts';
import type { TrustSubject, TrustSubjectsResponse } from './trusts';

export type { TrustSubject, TrustSubjectsResponse } from './trusts';

/** Default keystroke debounce — long enough to skip per-keystroke fetches,
 *  short enough to feel instant. Matches the 300ms used elsewhere in the app. */
export const SUBJECTS_SEARCH_DEBOUNCE_MS = 300;

export interface UseTrustSubjectsSearchOptions {
  /** The trust whose registers we search. Empty string → disabled. */
  trustId: string;
  /** Raw, un-debounced input text from the combobox. */
  query: string;
  /**
   * Master gate — usually the combobox's `open` flag. When false the query is
   * never issued (and any in-flight debounce is discarded). Defaults to true.
   */
  enabled?: boolean;
  /** Debounce window in ms. Defaults to {@link SUBJECTS_SEARCH_DEBOUNCE_MS}. */
  debounceMs?: number;
  /**
   * Minimum trimmed query length before the request fires. An empty query
   * (length 0) is always allowed so the open dropdown can list the whole
   * register; this floor only applies to *non-empty* queries. Default 1.
   */
  minChars?: number;
}

export interface UseTrustSubjectsSearchResult {
  /** The trimmed query currently committed to the request (post-debounce). */
  debouncedQuery: string;
  /** True once a non-empty query has been typed but not yet debounced/issued. */
  isDebouncing: boolean;
  data: TrustSubjectsResponse | undefined;
  subjects: TrustSubject[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Debounced wrapper around {@link useTrustSubjects}. Owns the keystroke
 * debounce + min-length gate so the combobox component stays a pure view.
 */
export function useTrustSubjectsSearch(
  opts: UseTrustSubjectsSearchOptions,
): UseTrustSubjectsSearchResult {
  const {
    trustId,
    query,
    enabled = true,
    debounceMs = SUBJECTS_SEARCH_DEBOUNCE_MS,
    minChars = 1,
  } = opts;

  const trimmed = query.trim();
  const [debouncedQuery, setDebouncedQuery] = useState(trimmed);

  // Advance the committed query only after the user pauses typing. Cleared on
  // every keystroke so the timer always reflects the latest input. When the
  // wrapper is disabled we still keep `debouncedQuery` in sync so re-opening
  // doesn't fire a stale request.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(trimmed), debounceMs);
    return () => clearTimeout(id);
  }, [trimmed, debounceMs]);

  // A non-empty query below the floor never reaches the network; an empty
  // query lists the whole (small, capped) register when the dropdown is open.
  const meetsFloor = debouncedQuery.length === 0 || debouncedQuery.length >= minChars;
  const queryEnabled = enabled && !!trustId && meetsFloor;

  const result = useTrustSubjects({
    trustId,
    q: debouncedQuery,
    enabled: queryEnabled,
  });

  return {
    debouncedQuery,
    // "Debouncing" = the user has typed something the request hasn't caught up
    // to yet. Drives the combobox's spinner during the quiet window.
    isDebouncing: enabled && trimmed !== debouncedQuery,
    data: result.data,
    subjects: result.data?.subjects ?? [],
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
    error: result.error,
  };
}
